import { WSInfoBox, WSList } from "@wingspanhq/fe-component-library";
import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces";
import { wsName } from "@wingspanhq/utils/dist/name/wsName";
import { useQueryInsuranceCoverages } from "../../../../../../query/insuranceCoverage/queries/useQueryInsuranceCoverages";
import { useQueryPayer } from "../../../../../../query/payers/queries/useQueryPayer";
import { WSQueries } from "../../../../../../query/WSQuery";
import type {
  insuranceCoverageApiSpec,
  insuranceMonitorApiSpec
} from "@wingspanhq/wingspan-api-types";

import { useAllFromInfiniteQuery } from "../../../../../../shared/hooks/useAllFromInfiniteQuery";
import { useGetNow } from "../../../../../../shared/hooks/useGetNow";
import { useFlowUploadDocument } from "../../../../components/FlowUploadDocument";
import { RequirementTableCallback } from "../../../../types";
import { CardPartialMatchSubmitForReview } from "../../components/CardPartialMatchSubmitForReview";
import { CardShareYourInformation } from "../../components/CardShareYourInformation";
import { CardUploadNewDocument } from "../../components/CardUploadNewDocument";
import { CardWingspanInsurance } from "../../components/CardWingspanInsurance";
import { EmptyStateNoMatch } from "../../components/EmptyStateNoMatch";
import { useModalMatchingInProgress } from "../../components/ModalMatchingInProgress";
import { INSURANCE_COVERAGE_REQUIREMENT_EVENT } from "../utils";
import { selectorIsAcceptedSourceTypeIncluded } from "../../selectors/selectorIsAcceptedSourceTypeIncluded";
import intersection from "lodash/intersection";
import { selectorPolicyTypesFromSettings } from "../../selectors/selectorPolicyTypesFromSettings";

type InsuranceCoverageMonitor =
  insuranceMonitorApiSpec.InsuranceCoverageMonitor;
type IPolicyType = insuranceCoverageApiSpec.InsuranceCoverage["policyType"];

type Props = {
  requirement: IRequirementResponse;
  insuranceCoverageMonitor: InsuranceCoverageMonitor;
  callback: RequirementTableCallback;
};
export const ProcessingDetails: React.FC<Props> = ({
  requirement,
  insuranceCoverageMonitor,
  callback
}) => {
  const now = useGetNow();
  const uploadFlow = useFlowUploadDocument({
    onSuccess() {
      callback(
        INSURANCE_COVERAGE_REQUIREMENT_EVENT.UploadCoverage,
        requirement
      );
    }
  });

  const modalConsent = useModalMatchingInProgress();
  const queryPayer = useQueryPayer(requirement.payerId);
  const queryInsuranceCoveragesAll = useQueryInsuranceCoverages({
    page: {
      size: 1
    }
  });

  const queryInsuranceCoveragesMatched = useQueryInsuranceCoverages({
    filter: {
      policyType: {
        in: selectorPolicyTypesFromSettings(insuranceCoverageMonitor.settings)
      }
    }
  });

  const queryAllInsuranceCoveragesMatched = useAllFromInfiniteQuery(
    queryInsuranceCoveragesMatched
  );

  return (
    <WSQueries
      queries={{
        queryPayer,
        queryInsuranceCoveragesAll,
        queryAllInsuranceCoveragesMatched
      }}
    >
      {({
        queryPayerData,
        queryInsuranceCoveragesAllData,
        queryAllInsuranceCoveragesMatchedData
      }) => {
        const policyTypes =
          selectorPolicyTypesFromSettings(insuranceCoverageMonitor.settings) ||
          [];

        const allCoverages = queryInsuranceCoveragesAllData.pages[0];
        const partialMatchCoverages = queryAllInsuranceCoveragesMatchedData;
        const validPartialMatchCoverages = partialMatchCoverages.filter(
          coverage => !coverage.expirationDate || coverage.expirationDate > now
        );

        const isNotShared =
          insuranceCoverageMonitor.status === "Pending" &&
          validPartialMatchCoverages.length > 0;

        const partialMatchLength = partialMatchCoverages.length;
        const isPartialMatch = partialMatchLength > 0;
        const noPoliciesMatch =
          allCoverages.length > 0 && partialMatchLength === 0;

        const isOpenEnrollmentAllowed = selectorIsAcceptedSourceTypeIncluded(
          "PlanEnrollment",
          insuranceCoverageMonitor.settings
        );

        const matchedToMasterPolicies = intersection(policyTypes, [
          "GeneralLiability",
          "OccupationalAccident",
          "ProfessionalLiability"
        ] as IPolicyType[]);

        const isWingspanInsuranceAd =
          matchedToMasterPolicies.length > 0 && isOpenEnrollmentAllowed;

        const payerNames = wsName({
          user: queryPayerData.user!,
          member: queryPayerData.member,
          payeeOwnedData: queryPayerData.payeeOwnedData
        });

        return (
          <WSList gap="L">
            {isNotShared ? (
              <CardShareYourInformation
                payerName={payerNames.getResolvedName()}
                onContinueClick={() =>
                  modalConsent.open({
                    payer: queryPayerData,
                    policyTypes: policyTypes,
                    insuranceCoverageMonitorId:
                      insuranceCoverageMonitor.insuranceCoverageMonitorId
                  })
                }
              />
            ) : (
              <>
                {noPoliciesMatch ? (
                  <EmptyStateNoMatch />
                ) : isPartialMatch ? (
                  <WSInfoBox>
                    There are currently {partialMatchLength} policies in your
                    “My resources” section that partially match this
                    requirement. Review the instructions and your options below
                    to take action.
                  </WSInfoBox>
                ) : (
                  <WSInfoBox>
                    There are currently no policies in your “My resources”
                    section that match this requirement. Review the instructions
                    and your options below to take action.
                  </WSInfoBox>
                )}

                {isPartialMatch && (
                  <CardPartialMatchSubmitForReview
                    coverages={partialMatchCoverages}
                    coverageMonitor={insuranceCoverageMonitor}
                    requirement={requirement}
                    callback={callback}
                  />
                )}

                {isWingspanInsuranceAd && (
                  <CardWingspanInsurance
                    policyType={matchedToMasterPolicies[0]!}
                  />
                )}

                <CardUploadNewDocument
                  onUploadClick={() => {
                    uploadFlow.open();
                  }}
                />
              </>
            )}
          </WSList>
        );
      }}
    </WSQueries>
  );
};
